import { ActivityFeedTime } from "@volca/ui/components/ui/activity-feed";
import { count } from "@volca/ui/trades";
import { dayjs } from "@/core/utils/date";
import { SectionHeading } from "@/profitability/records/section-heading";
import type { ProfitabilityRun } from "@/profitability/types";
import type { RunReview } from "./period-review";

interface Event {
  at: string;
  text: string;
}

/**
 * The period's own history, newest first: when the report ran, what Volca
 * flagged, when someone signed it off, and a newer run still building.
 */
export const PeriodTimeline = ({
  run,
  running,
  flagged,
  review,
}: {
  run: ProfitabilityRun;
  running: ProfitabilityRun | null;
  /** How many records the report flagged, and what it calls them. */
  flagged: { count: number; noun: string; plural: string } | null;
  review: RunReview | null;
}) => {
  const events: Event[] = [];
  const ran = run.completed_at ?? run.created_at;
  if (ran) {
    events.push({ at: ran, text: "The report ran" });
    if (flagged?.count)
      events.push({
        at: ran,
        // Only what certainly happened: a guess at the cause is the model's,
        // and shows on the findings that have one.
        text: `Volca flagged ${count(flagged.count, flagged.noun, flagged.plural)}`,
      });
  }
  if (review)
    events.push({
      at: review.reviewed_at,
      text: `${review.reviewed_by?.full_name ?? "Someone"} marked it reviewed`,
    });
  if (running?.created_at)
    events.push({ at: running.created_at, text: "A newer run started" });
  if (!events.length) return null;
  const newest = [...events].sort((a, b) => b.at.localeCompare(a.at));
  return (
    <section
      aria-label="Timeline"
      className="@container/timeline flex min-w-0 flex-col gap-2"
    >
      <SectionHeading>Timeline</SectionHeading>
      <ol className="relative flex flex-col gap-3 before:absolute before:top-1.5 before:bottom-1.5 before:left-[3px] before:w-px before:bg-line">
        {newest.map((event) => (
          <li
            key={`${event.at}-${event.text}`}
            className="relative grid min-w-0 grid-cols-1 items-start gap-x-3 gap-y-0.5 pl-4 text-sm @sm/timeline:grid-cols-[minmax(0,1fr)_auto]"
          >
            <span
              aria-hidden
              className="absolute top-1.5 left-0 size-[7px] rounded-full border border-line-strong bg-surface"
            />
            <span className="min-w-0 flex-1 text-ink">{event.text}</span>
            <ActivityFeedTime
              dateTime={event.at}
              className="ml-0 pl-0 text-xs text-subtle @sm/timeline:justify-self-end"
            >
              {dayjs(event.at).format("MMM D, h:mm a")}
            </ActivityFeedTime>
          </li>
        ))}
      </ol>
    </section>
  );
};
